import { useMemo } from 'react'
import moment, { Moment } from 'moment-timezone'

import { PERIOD_MIN_YEAR, isPeriodRangeValid } from '../consumptionCertificate.dto'

const PERIOD_DATE_FORMAT = 'DD.MM.YYYY'

/** A usable picker date: valid and inside the allowed window, else null so it never bounds the other picker. */
const usableBound = (date: Moment | null | undefined, min: Moment, max: Moment): Moment | null =>
  date?.isValid() && !date.isBefore(min) && !date.isAfter(max) ? date : null

/**
 * Bounds for a start/end date-picker pair of a billing or vacancy period, and whether the picked pair is invalid.
 * Each picker is bounded by the other date only when that date is usable, so an invalid or out-of-range date
 * never inverts a picker's window and disables every day.
 */
export const usePeriodPickerBounds = (start: Moment | null | undefined, end: Moment | null | undefined) => {
  const min = useMemo(() => moment({ year: PERIOD_MIN_YEAR, month: 0, day: 1 }), [])
  const max = useMemo(() => moment().endOf('year'), [])

  return useMemo(() => {
    const usableStart = usableBound(start, min, max)
    const usableEnd = usableBound(end, min, max)
    return {
      startMinDate: min,
      startMaxDate: usableEnd ?? max,
      endMinDate: usableStart ?? min,
      endMaxDate: max,
      /** Both dates entered but unparseable, out of order or out of range — flagged at once, not only after Next. */
      isRangeInvalid: !!start && !!end
        && !isPeriodRangeValid(start.isValid() ? start.format(PERIOD_DATE_FORMAT) : '', end.isValid() ? end.format(PERIOD_DATE_FORMAT) : ''),
    }
  }, [start, end, min, max])
}
